import { appConfig } from '@/configs';
import { getStandardizedApiError } from '@/services/client/Error';
import { ExtendedAxiosRequestConfig } from '@/services/client/types';
import { authStorage } from '@/services/storage/AuthStorage';
import { type AxiosError, AxiosResponse } from 'axios';
import { client } from '../client';

export const refreshTokenUrl = `${appConfig.apiURL}/refresh-token`;

export type RefreshTokenMutationResponse = {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiry: number;
  fullName: string;
  email: string;
  profileImage: string;
};

// Global flag to prevent multiple simultaneous refresh attempts
let refreshPromise: Promise<RefreshTokenMutationResponse> | null = null;

export const responseSuccessInterceptor = (response: AxiosResponse) => response;
export function getOrCreateRefreshPromise() {
  if (!refreshPromise) {
    refreshPromise = performTokenRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}
export const responseFailureInterceptor = async (error: AxiosError) => {
  const standarizedError = getStandardizedApiError(error);
  const originalRequest = error.config as ExtendedAxiosRequestConfig;

  // Only handle 401 errors
  if (standarizedError.statusCode !== 401) {
    return Promise.reject(standarizedError);
  }

  // Ensure we have a valid original request
  if (!originalRequest) {
    return Promise.reject(standarizedError);
  }

  // Skip token refresh logic for login/signup endpoints
  const isAuthEndpoint =
    originalRequest.url?.includes('/login') ||
    originalRequest.url?.includes('/signup') ||
    originalRequest.url?.includes('/refresh-token');

  if (isAuthEndpoint) {
    // Just reject the error without logout/redirect for auth endpoints
    return Promise.reject(standarizedError);
  }

  // Prevent infinite loops for refresh token requests
  if (originalRequest.url === refreshTokenUrl || originalRequest.url?.includes('/refresh-token')) {
    logoutAndRedirect();
    return Promise.reject(standarizedError);
  }

  // If already retried once, logout
  if (originalRequest._retry) {
    logoutAndRedirect();
    return Promise.reject(standarizedError);
  }

  // Check if we have a refresh token
  if (!authStorage.refreshToken) {
    logoutAndRedirect();
    return Promise.reject(standarizedError);
  }

  // Mark request as retried
  originalRequest._retry = true;

  try {
    const refreshData = await getOrCreateRefreshPromise();

    originalRequest.headers = originalRequest.headers ?? {};
    originalRequest.headers.Authorization = `Bearer ${refreshData.accessToken}`;

    return client(originalRequest);
  } catch {
    logoutAndRedirect();
    return Promise.reject(standarizedError);
  }
};

async function performTokenRefresh(): Promise<RefreshTokenMutationResponse> {
  const {
    data: { data },
  } = await client.post<{ data: RefreshTokenMutationResponse }>(refreshTokenUrl, {
    refreshToken: authStorage.refreshToken,
  });

  // Validate response structure
  if (!data.accessToken || !data.refreshToken || typeof data.accessTokenExpiry !== 'number') {
    throw new Error('Invalid refresh token response structure');
  }

  // Update auth storage with new tokens
  authStorage.accessToken = data.accessToken;
  authStorage.expires = data.accessTokenExpiry;
  authStorage.refreshToken = data.refreshToken;
  authStorage.fullName = data.fullName;
  authStorage.email = data.email;
  authStorage.profileImage = data.profileImage;

  // Notify React app about the refreshed tokens so it can sync its internal state
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('auth-token-refreshed', { detail: data }));
  }

  return data;
}

function logoutAndRedirect() {
  // Clear refresh state
  refreshPromise = null;

  // Clear tokens
  authStorage.accessToken = null;
  authStorage.expires = null;
  authStorage.refreshToken = null;

  // Redirect to login
  window.location.replace('/login');
}

// Export utility functions for external use if needed
export { logoutAndRedirect, performTokenRefresh };
